<template>
    <hts-drawer
        ref="bookingDrawer"
        v-model="showBookingDrawer"
        :submit="handleBooking"
        title="Đặt xe"
        :size="70"
        @close="closeBookingDrawer">
        <div
            v-if="showBookingDrawer"
            :style="{ minWidth: minWidthScroll }">
            <hts-card title="Thông tin vận chuyển">
                <div class="space-y-4">
                    <div class="flex items-center justify-between border-b pb-3">
                        <div class="flex items-center">
                            <hi-arrow-up-circle-icon class="h-7 w-7 text-blue-600 mr-2" />
                            <span class="font-medium">
                                {{ bookingForm.originName }}
                            </span>
                        </div>
                        <hi-arrow-right-icon class="h-7 w-7 text-gray-400 mx-2" />
                        <div class="flex items-center">
                            <hi-flag-icon class="h-7 w-7 text-green-600 mr-2" />
                            <span class="font-medium">
                                {{ bookingForm.destinationName }}
                            </span>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div class="flex items-center space-x-2">
                            <hi-truck-icon
                                v-if="bookingForm.transportation === 'NON_CONTAINER'"
                                class="h-7 w-7 text-gray-600" />
                            <hi-rectangle-stack-icon
                                v-else
                                class="h-7 w-7 text-gray-600" />
                            <span>
                                {{ getLabel(transportations, bookingForm.transportation) }}
                            </span>
                        </div>
                        <div class="flex items-center space-x-2">
                            Loại:
                            <span>
                                {{
                                    bookingForm.transportation === 'CONTAINER'
                                        ? getLabel(containerTypes, bookingForm.containerType)
                                        : getLabel(vehicleCategories, bookingForm.vehicleCategory)
                                }}
                            </span>
                        </div>
                        <div class="flex items-center space-x-2">
                            <hi-map-icon class="h-7 w-7 text-gray-600" />
                            <span>
                                {{ bookingForm.routingPositions?.route?.distance }} Km
                            </span>
                        </div>

                        <div class="flex items-center space-x-2">
                            <hi-currency-dollar-icon class="h-7 w-7 text-gray-600" />
                            <span>
                                {{ $formatters.formatCurrency(bookingForm.price) }} VNĐ
                            </span>
                        </div>
                    </div>

                    <div class="border-t pt-3">
                        <div class="grid grid-cols-2 gap-4 items-center">
                            <hts-form-input
                                v-model="bookingForm.bookingQuantity"
                                label="Số lượng"
                                type="number"
                                :min="1"
                                required />

                            <div class="flex flex-col">
                                <span class="text-sm text-gray-500 mb-1">
                                    Tổng giá
                                </span>
                                <div class="text-xl font-bold text-blue-600">
                                    {{ $formatters.formatCurrency(bookingForm.bookingQuantity * (bookingForm.price || 0)) }} VNĐ
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </hts-card>

            <hts-card title="Thông tin liên hệ">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        v-model="bookingForm.contactName"
                        label="Họ tên"
                        required
                        placeholder="Nhập họ tên người liên hệ" />
                    <hts-form-input
                        v-model="bookingForm.contactPhone"
                        label="Số điện thoại"
                        required
                        placeholder="Nhập số điện thoại" />
                    <hts-form-input
                        v-model="bookingForm.contactEmail"
                        label="Email"
                        placeholder="Nhập email liên hệ" />
                </div>
            </hts-card>

            <hts-card title="Thông tin hàng hóa">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        v-model="bookingForm.commodityName"
                        label="Hàng hóa" />
                    <hts-form-input
                        v-model="bookingForm.goodsWeight"
                        label="Trọng lượng hàng (kg)"
                        type="number"
                        placeholder="Nhập trọng lượng hàng hóa" />
                    <hts-form-textarea
                        v-model="bookingForm.goodsDescription"
                        label="Mô tả hàng hóa"
                        class="col-span-2"
                        placeholder="Mô tả chi tiết về hàng hóa" />
                </div>
            </hts-card>

            <hts-card title="Thông tin bổ sung">
                <hts-form-textarea
                    v-model="bookingForm.note"
                    label="Ghi chú"
                    placeholder="Nhập ghi chú hoặc yêu cầu đặc biệt nếu có" />
            </hts-card>
        </div>
    </hts-drawer>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import { $api } from '#imports'
import containerTypes from '~/data/select/containerTypes.json'
import transportations from '~/data/select/transportations.json'
import vehicleCategories from '~/data/select/vehicleCategories.json'

const store = $store()
const { $formatters, $disableReactive } = useNuxtApp()

// UI state
const showBookingDrawer = ref(false)
const loadingBooking = ref(false)
const minWidthScroll = '600px'

// Props
const props = defineProps({
  type: {
    type: String,
    default: 'user' // user | guest
  }
})
const { type } = toRefs(props)

// API config theo loại user/guest
const apiBooking = computed(() => {
  if (type.value === 'user') {
    return { method: 'post', url: '/user/create-booking' }
  }
  if (type.value === 'guest') {
    return { method: 'post', url: '/guest/create-booking' }
  }
})

// Computed từ store
const guestId = computed(() => store.guest.getGuestId)
const contactName = computed(() => store.guest.getContactName)
const contactPhone = computed(() => store.guest.getContactPhone)
const contactEmail = computed(() => store.guest.getContactEmail)
const commodityName = computed(() => store.guest.getCommodityName)
const goodsWeight = computed(() => store.guest.getGoodsWeight)
const goodsDescription = computed(() => store.guest.getGoodsDescription)

// Booking form data
const bookingForm = reactive({
  guestId: '',
  origin: null,
  originName: '',
  destination: null,
  destinationName: '',
  shipmentType: '',
  transportation: '',
  containerType: '',
  vehicleCategory: '',
  date: '',
  price: 0,
  tasks: [] as any[],
  priceInfo: null as { [key: string]: any } | null,
  bookingQuantity: 1,
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  commodityName: '',
  goodsWeight: 0,
  goodsDescription: '',
  searchResultId: '',
  routingPositions: null as { [key: string]: any } | null,
  note: ''
})

// Helper get label
const getLabel = (list: { value: string | number; label: string }[], value: string | number): string => {
  const found = list.find((item) => item.value === value)
  return found ? found.label : ''
}

// Xử lý gửi đơn đặt xe
const handleBooking = async () => {
  if (!apiBooking.value) { return }
  loadingBooking.value = true

  const data: any = await $api(apiBooking.value, { body: bookingForm })
  const resData = data?.data?.value

  if (resData?.success) {
    store.guest.setGuestInfo($disableReactive(bookingForm))
    process.client && $toast().success('Đặt xe thành công!')
    showBookingDrawer.value = false
  }
}

// Mở drawer đặt xe với dữ liệu được truyền vào
const openBookingDrawer = (price: any) => {
  if (guestId.value) {
    bookingForm.guestId = guestId.value
    bookingForm.contactName = contactName.value
    bookingForm.contactPhone = contactPhone.value
    bookingForm.contactEmail = contactEmail.value
    bookingForm.commodityName = commodityName.value
    bookingForm.goodsWeight = goodsWeight.value
    bookingForm.goodsDescription = goodsDescription.value
  }

  bookingForm.origin = price.origin
  bookingForm.originName = price.originName
  bookingForm.destination = price.destination
  bookingForm.destinationName = price.destinationName
  bookingForm.shipmentType = price.shipmentType
  bookingForm.transportation = price.transportation
  bookingForm.containerType = price.containerType
  bookingForm.vehicleCategory = price.vehicleCategory
  bookingForm.date = price.date
  bookingForm.priceInfo = price.priceInfo
  bookingForm.searchResultId = price.searchResultId
  bookingForm.routingPositions = price.routingPositions
  bookingForm.price = price.price || 0
  bookingForm.tasks = price.tasks || []
  showBookingDrawer.value = true
}

// Đóng drawer (hiện tại chỉ ẩn)
const closeBookingDrawer = () => {
  // Optional: reset nếu cần
}

defineExpose({
  openBookingDrawer
})
</script>
